import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  ChevronDown, Layers, Percent, Banknote, Timer, Trophy,
  ToggleLeft, ToggleRight, Plus, Trash2, Save, Check, Pencil,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { cardReveal } from '@/lib/motion'
import { Drawer } from 'vaul'

type SectionKey = 'stakes' | 'rake' | 'withdrawal' | 'timer' | 'jackpot'

interface StakeLevel {
  stakes: string
  minBuy: number
  maxBuy: number
  rake: number
  enabled: boolean
}

const initialStakeLevels: StakeLevel[] = [
  { stakes: '1/2', minBuy: 40, maxBuy: 200, rake: 5, enabled: true },
  { stakes: '2/5', minBuy: 100, maxBuy: 500, rake: 5, enabled: true },
  { stakes: '5/10', minBuy: 200, maxBuy: 1000, rake: 4, enabled: true },
  { stakes: '10/20', minBuy: 400, maxBuy: 2000, rake: 3, enabled: true },
  { stakes: '25/50', minBuy: 1000, maxBuy: 5000, rake: 2.5, enabled: false },
]

const timerOptions = [15, 20, 30, 45, 60]

interface JackpotTier {
  hand: string
  pct: number
  color: string
}

const initialJackpotTiers: JackpotTier[] = [
  { hand: 'Royal Flush', pct: 50, color: 'text-gold' },
  { hand: 'Straight Flush', pct: 25, color: 'text-amber-400' },
  { hand: 'Four of a Kind', pct: 15, color: 'text-blue-400' },
  { hand: 'Reserve', pct: 10, color: 'text-muted-foreground' },
]

const sections: { key: SectionKey; title: string; icon: typeof Layers }[] = [
  { key: 'stakes', title: '盲注級別管理', icon: Layers },
  { key: 'rake', title: '抽水設定', icon: Percent },
  { key: 'withdrawal', title: '提款設定', icon: Banknote },
  { key: 'timer', title: '計時器設定', icon: Timer },
  { key: 'jackpot', title: '累積獎金設定', icon: Trophy },
]

// Inline editable number field
function EditableField({
  value,
  onChange,
  suffix = '',
  prefix = '',
  min,
  max,
}: {
  value: number
  onChange: (val: number) => void
  suffix?: string
  prefix?: string
  min?: number
  max?: number
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(String(value))

  const commit = () => {
    let num = parseFloat(draft)
    if (isNaN(num)) num = value
    if (min !== undefined) num = Math.max(min, num)
    if (max !== undefined) num = Math.min(max, num)
    onChange(num)
    setDraft(String(num))
    setEditing(false)
  }

  if (editing) {
    return (
      <div className="flex items-center gap-1">
        {prefix && <span className="text-xs text-muted-foreground">{prefix}</span>}
        <input
          autoFocus
          type="number"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => e.key === 'Enter' && commit()}
          className="w-20 h-7 px-2 bg-surface-elevated border border-gold/40 rounded-lg text-sm font-bold font-mono text-gold text-right focus:outline-none focus:border-gold"
        />
        {suffix && <span className="text-xs text-muted-foreground">{suffix}</span>}
      </div>
    )
  }

  return (
    <button
      onClick={() => { setDraft(String(value)); setEditing(true) }}
      className="flex items-center gap-1.5 group"
    >
      <span className="text-sm font-bold font-mono text-gold group-hover:text-gold/80 transition-colors">
        {prefix}{value}{suffix}
      </span>
      <Pencil className="w-3 h-3 text-muted-foreground/0 group-hover:text-muted-foreground/60 transition-all" />
    </button>
  )
}

export function AdminConfigScreen() {
  const [expanded, setExpanded] = useState<SectionKey | null>('stakes')
  const [stakeLevels, setStakeLevels] = useState(initialStakeLevels)
  const [activeTimer, setActiveTimer] = useState(2) // index of 30s
  const [saved, setSaved] = useState(false)
  const [addStakeOpen, setAddStakeOpen] = useState(false)
  const [newStake, setNewStake] = useState({ small: '', big: '', minBuy: '', maxBuy: '', rake: '5' })

  // Rake config
  const [rakeConfig, setRakeConfig] = useState({
    baseRate: 5,
    minPerHand: 0.10,
    maxPerHand: 3.00,
    agentCap: 5,
  })

  // Withdrawal config
  const [withdrawConfig, setWithdrawConfig] = useState({
    feeRate: 1,
    minFee: 1.00,
    dailyLimit: 10000,
    processingHours: 24,
    minAmount: 10.00,
  })

  // Timer config
  const [disconnectTimeout, setDisconnectTimeout] = useState(60)
  const [autoLeaveRounds, setAutoLeaveRounds] = useState(3)

  // Jackpot config
  const [jackpotTiers, setJackpotTiers] = useState(initialJackpotTiers)
  const [jackpotContribution, setJackpotContribution] = useState(1)

  const toggle = (key: SectionKey) => setExpanded(expanded === key ? null : key)

  const toggleStake = (i: number) => {
    setStakeLevels((prev) => prev.map((s, idx) => idx === i ? { ...s, enabled: !s.enabled } : s))
  }

  const removeStake = (i: number) => {
    setStakeLevels((prev) => prev.filter((_, idx) => idx !== i))
  }

  const updateStake = (i: number, field: keyof StakeLevel, value: number) => {
    setStakeLevels((prev) => prev.map((s, idx) => idx === i ? { ...s, [field]: value } : s))
  }

  const addStake = () => {
    const small = parseInt(newStake.small)
    const big = parseInt(newStake.big)
    if (!small || !big) return
    setStakeLevels((prev) => [...prev, {
      stakes: `${small}/${big}`,
      minBuy: parseInt(newStake.minBuy) || big * 20,
      maxBuy: parseInt(newStake.maxBuy) || big * 100,
      rake: parseFloat(newStake.rake) || 5,
      enabled: true,
    }])
    setNewStake({ small: '', big: '', minBuy: '', maxBuy: '', rake: '5' })
    setAddStakeOpen(false)
  }

  const updateJackpotTier = (i: number, pct: number) => {
    setJackpotTiers((prev) => prev.map((t, idx) => idx === i ? { ...t, pct } : t))
  }

  const handleSave = () => {
    setSaved(true)
    setTimeout(() => setSaved(false), 2000)
  }

  const jackpotTotal = jackpotTiers.reduce((s, t) => s + t.pct, 0)

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={{ hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.06 } } }}
      className="p-4 pb-24 flex flex-col gap-3"
    >
      {sections.map(({ key, title, icon: Icon }) => (
        <motion.div key={key} variants={cardReveal} className="rounded-2xl glass-card overflow-hidden">
          {/* Section Header */}
          <button
            onClick={() => toggle(key)}
            className="w-full flex items-center justify-between p-4 active:bg-foreground/5 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-gold/10 flex items-center justify-center">
                <Icon className="w-4 h-4 text-gold" />
              </div>
              <span className="text-sm font-bold text-foreground">{title}</span>
            </div>
            <ChevronDown className={cn('w-4 h-4 text-muted-foreground transition-transform duration-200', expanded === key && 'rotate-180')} />
          </button>

          {/* ========== STAKE LEVELS ========== */}
          <AnimatePresence>
            {expanded === 'stakes' && key === 'stakes' && (
              <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
                <div className="px-4 pb-4 space-y-2">
                  <div className="grid grid-cols-[1fr_1fr_1fr_0.7fr_auto_auto] gap-2 px-2 text-[9px] text-muted-foreground uppercase tracking-wider">
                    <span>盲注</span><span>最低買入</span><span>最高買入</span><span>抽水</span><span>狀態</span><span></span>
                  </div>
                  {stakeLevels.map((s, i) => (
                    <div key={s.stakes} className="grid grid-cols-[1fr_1fr_1fr_0.7fr_auto_auto] gap-2 items-center p-2.5 rounded-xl bg-surface-elevated">
                      <span className="text-xs font-bold font-mono text-gold">${s.stakes}</span>
                      <EditableField value={s.minBuy} onChange={(v) => updateStake(i, 'minBuy', v)} prefix="$" min={1} />
                      <EditableField value={s.maxBuy} onChange={(v) => updateStake(i, 'maxBuy', v)} prefix="$" min={s.minBuy} />
                      <EditableField value={s.rake} onChange={(v) => updateStake(i, 'rake', v)} suffix="%" min={0} max={10} />
                      <button onClick={() => toggleStake(i)} className="flex-shrink-0">
                        {s.enabled
                          ? <ToggleRight className="w-6 h-6 text-emerald-400" />
                          : <ToggleLeft className="w-6 h-6 text-muted-foreground/40" />
                        }
                      </button>
                      <button onClick={() => removeStake(i)} className="flex-shrink-0 p-1 rounded hover:bg-rose-500/10 transition-colors">
                        <Trash2 className="w-3.5 h-3.5 text-muted-foreground/40 hover:text-rose-400" />
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={() => setAddStakeOpen(true)}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-dashed border-foreground/10 hover:border-gold/30 text-muted-foreground hover:text-gold transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    <span className="text-xs font-bold">新增盲注級別</span>
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* ========== RAKE CONFIG ========== */}
          <AnimatePresence>
            {expanded === 'rake' && key === 'rake' && (
              <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
                <div className="px-4 pb-4 space-y-2">
                  <div className="flex items-center justify-between p-3 rounded-xl bg-surface-elevated">
                    <span className="text-xs text-muted-foreground">平台基礎抽水率</span>
                    <EditableField value={rakeConfig.baseRate} onChange={(v) => setRakeConfig(c => ({ ...c, baseRate: v }))} suffix="%" min={0} max={20} />
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-xl bg-surface-elevated">
                    <span className="text-xs text-muted-foreground">每手最低抽水</span>
                    <EditableField value={rakeConfig.minPerHand} onChange={(v) => setRakeConfig(c => ({ ...c, minPerHand: v }))} prefix="$" min={0} />
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-xl bg-surface-elevated">
                    <span className="text-xs text-muted-foreground">每手最高抽水</span>
                    <EditableField value={rakeConfig.maxPerHand} onChange={(v) => setRakeConfig(c => ({ ...c, maxPerHand: v }))} prefix="$" min={rakeConfig.minPerHand} />
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-xl bg-surface-elevated">
                    <span className="text-xs text-muted-foreground">代理房間額外上限</span>
                    <EditableField value={rakeConfig.agentCap} onChange={(v) => setRakeConfig(c => ({ ...c, agentCap: v }))} suffix="%" min={0} max={10} />
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* ========== WITHDRAWAL CONFIG ========== */}
          <AnimatePresence>
            {expanded === 'withdrawal' && key === 'withdrawal' && (
              <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
                <div className="px-4 pb-4 space-y-2">
                  <div className="flex items-center justify-between p-3 rounded-xl bg-surface-elevated">
                    <span className="text-xs text-muted-foreground">手續費率</span>
                    <EditableField value={withdrawConfig.feeRate} onChange={(v) => setWithdrawConfig(c => ({ ...c, feeRate: v }))} suffix="%" min={0} max={10} />
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-xl bg-surface-elevated">
                    <span className="text-xs text-muted-foreground">最低手續費</span>
                    <EditableField value={withdrawConfig.minFee} onChange={(v) => setWithdrawConfig(c => ({ ...c, minFee: v }))} prefix="$" min={0} />
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-xl bg-surface-elevated">
                    <span className="text-xs text-muted-foreground">每日提款上限</span>
                    <EditableField value={withdrawConfig.dailyLimit} onChange={(v) => setWithdrawConfig(c => ({ ...c, dailyLimit: v }))} prefix="$" min={100} />
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-xl bg-surface-elevated">
                    <span className="text-xs text-muted-foreground">處理時間</span>
                    <EditableField value={withdrawConfig.processingHours} onChange={(v) => setWithdrawConfig(c => ({ ...c, processingHours: v }))} suffix=" 小時" min={1} max={72} />
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-xl bg-surface-elevated">
                    <span className="text-xs text-muted-foreground">最低提款金額</span>
                    <EditableField value={withdrawConfig.minAmount} onChange={(v) => setWithdrawConfig(c => ({ ...c, minAmount: v }))} prefix="$" min={1} />
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* ========== TIMER CONFIG ========== */}
          <AnimatePresence>
            {expanded === 'timer' && key === 'timer' && (
              <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
                <div className="px-4 pb-4 space-y-3">
                  <div className="space-y-2">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider">回合計時（秒）</p>
                    <div className="grid grid-cols-5 gap-2">
                      {timerOptions.map((opt, i) => {
                        const timerLabel = opt <= 20 ? '快速' : opt <= 30 ? '標準' : '深度'
                        return (
                          <button
                            key={opt}
                            onClick={() => setActiveTimer(i)}
                            className={cn(
                              'py-2 rounded-xl border transition-colors flex flex-col items-center gap-0.5',
                              activeTimer === i
                                ? 'border-gold/40 bg-gold/10 text-gold'
                                : 'border-foreground/10 bg-surface-elevated text-muted-foreground hover:border-foreground/20'
                            )}
                          >
                            <span className="text-xs font-bold font-mono">{opt}s</span>
                            <span className={cn("text-[8px] font-medium", activeTimer === i ? "text-gold/60" : "text-muted-foreground/40")}>{timerLabel}</span>
                          </button>
                        )
                      })}
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-xl bg-surface-elevated">
                    <span className="text-xs text-muted-foreground">斷線逾時</span>
                    <EditableField value={disconnectTimeout} onChange={setDisconnectTimeout} suffix=" 秒" min={10} max={300} />
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-xl bg-surface-elevated">
                    <span className="text-xs text-muted-foreground">自動離座（連續未操作）</span>
                    <EditableField value={autoLeaveRounds} onChange={setAutoLeaveRounds} suffix=" 回合" min={1} max={10} />
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* ========== JACKPOT CONFIG ========== */}
          <AnimatePresence>
            {expanded === 'jackpot' && key === 'jackpot' && (
              <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
                <div className="px-4 pb-4 space-y-3">
                  <div className="flex items-center justify-between p-3 rounded-xl bg-surface-elevated">
                    <span className="text-xs text-muted-foreground">彩池貢獻率</span>
                    <EditableField value={jackpotContribution} onChange={setJackpotContribution} suffix="% of pot" min={0} max={5} />
                  </div>
                  <div className="space-y-2">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider">獎金分配層級</p>
                    {jackpotTiers.map((tier, i) => (
                      <div key={tier.hand} className="flex items-center justify-between p-3 rounded-xl bg-surface-elevated">
                        <span className="text-xs text-foreground">{tier.hand}</span>
                        <EditableField value={tier.pct} onChange={(v) => updateJackpotTier(i, v)} suffix="%" min={0} max={100} />
                      </div>
                    ))}
                    {jackpotTotal !== 100 && (
                      <div className={cn(
                        "flex items-center justify-between p-3 rounded-xl border",
                        jackpotTotal > 100 ? "bg-rose-500/10 border-rose-500/30" : "bg-amber-500/10 border-amber-500/30"
                      )}>
                        <span className="text-xs font-bold text-foreground">總計</span>
                        <span className={cn("text-sm font-bold font-mono", jackpotTotal > 100 ? "text-rose-400" : "text-amber-400")}>
                          {jackpotTotal}% {jackpotTotal !== 100 && (jackpotTotal > 100 ? '(超出)' : '(不足)')}
                        </span>
                      </div>
                    )}
                    {jackpotTotal === 100 && (
                      <div className="flex items-center justify-between p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30">
                        <span className="text-xs font-bold text-foreground">總計</span>
                        <span className="text-sm font-bold font-mono text-emerald-400 flex items-center gap-1">
                          <Check className="w-3.5 h-3.5" /> 100%
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center justify-between p-4 rounded-xl bg-gold/5 border border-gold/20">
                    <span className="text-xs font-bold text-foreground">當前獎池</span>
                    <span className="text-xl font-bold font-mono text-gold">$88,888</span>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      ))}

      {/* Floating Save Button */}
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50">
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={handleSave}
          className={cn(
            "flex items-center gap-2 px-8 py-3.5 rounded-2xl font-bold text-sm shadow-2xl transition-all",
            saved
              ? "bg-emerald-500 text-white shadow-emerald-500/30"
              : "bg-gold text-background shadow-gold/30 hover:shadow-gold/50"
          )}
        >
          {saved ? (
            <>
              <Check className="w-4 h-4" />
              已儲存
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              儲存變更
            </>
          )}
        </motion.button>
      </div>

      {/* Add Stake Level Drawer */}
      <Drawer.Root open={addStakeOpen} onOpenChange={setAddStakeOpen}>
        <Drawer.Portal>
          <Drawer.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" />
          <Drawer.Content className="fixed bottom-0 left-0 right-0 z-50 outline-none">
            <div className="bg-surface-elevated rounded-t-3xl border-t border-foreground/10 p-6">
              <div className="w-10 h-1 rounded-full bg-foreground/20 mx-auto mb-6" />
              <Drawer.Title className="text-lg font-bold text-foreground mb-6 flex items-center gap-2">
                <Plus className="w-5 h-5 text-gold" />
                新增盲注級別
              </Drawer.Title>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1 block">小盲</label>
                    <input
                      type="number"
                      value={newStake.small}
                      onChange={(e) => setNewStake(s => ({ ...s, small: e.target.value }))}
                      placeholder="e.g. 50"
                      className="w-full h-11 px-3 bg-surface-elevated border border-foreground/10 rounded-xl text-sm font-mono text-foreground placeholder:text-muted-foreground/30 focus:outline-none focus:border-gold/50"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1 block">大盲</label>
                    <input
                      type="number"
                      value={newStake.big}
                      onChange={(e) => setNewStake(s => ({ ...s, big: e.target.value }))}
                      placeholder="e.g. 100"
                      className="w-full h-11 px-3 bg-surface-elevated border border-foreground/10 rounded-xl text-sm font-mono text-foreground placeholder:text-muted-foreground/30 focus:outline-none focus:border-gold/50"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1 block">最低買入 ($)</label>
                    <input
                      type="number"
                      value={newStake.minBuy}
                      onChange={(e) => setNewStake(s => ({ ...s, minBuy: e.target.value }))}
                      placeholder="自動計算"
                      className="w-full h-11 px-3 bg-surface-elevated border border-foreground/10 rounded-xl text-sm font-mono text-foreground placeholder:text-muted-foreground/30 focus:outline-none focus:border-gold/50"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1 block">最高買入 ($)</label>
                    <input
                      type="number"
                      value={newStake.maxBuy}
                      onChange={(e) => setNewStake(s => ({ ...s, maxBuy: e.target.value }))}
                      placeholder="自動計算"
                      className="w-full h-11 px-3 bg-surface-elevated border border-foreground/10 rounded-xl text-sm font-mono text-foreground placeholder:text-muted-foreground/30 focus:outline-none focus:border-gold/50"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1 block">抽水率 (%)</label>
                  <input
                    type="number"
                    value={newStake.rake}
                    onChange={(e) => setNewStake(s => ({ ...s, rake: e.target.value }))}
                    className="w-full h-11 px-3 bg-surface-elevated border border-foreground/10 rounded-xl text-sm font-mono text-foreground focus:outline-none focus:border-gold/50"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setAddStakeOpen(false)}
                  className="flex-1 h-12 rounded-xl bg-surface-elevated text-foreground font-bold text-sm border border-foreground/10 hover:bg-foreground/10 transition-colors"
                >
                  取消
                </button>
                <button
                  onClick={addStake}
                  disabled={!newStake.small || !newStake.big}
                  className="flex-1 h-12 rounded-xl bg-gold text-background font-bold text-sm disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                >
                  新增
                </button>
              </div>
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    </motion.div>
  )
}
